import { useReducer, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser } from '@aws-amplify/auth';
import * as registrationService from '../services/registration/registrationService';

// Action types for the reducer
export const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_EVENT: 'SET_EVENT',
  SET_STEPS: 'SET_STEPS',
  SET_CURRENT_STEP_INDEX: 'SET_CURRENT_STEP_INDEX',
  SET_REGISTRATION_ID: 'SET_REGISTRATION_ID',
  SET_USER_ID: 'SET_USER_ID',
  SET_CHECKING_REGISTRATION: 'SET_CHECKING_REGISTRATION',
  SET_SETTINGS: 'SET_SETTINGS',
  SET_USER_INTERACTED: 'SET_USER_INTERACTED',
  UPDATE_REGISTRATION_DATA: 'UPDATE_REGISTRATION_DATA',
  SET_REG_PARTICIPANTS: 'SET_REG_PARTICIPANTS',
};

// Steps that should not be persisted to the backend
const SKIP_PERSISTENCE_STEPS = ["success"];

// Initial state for the reducer
const initialState = {
  event: null,
  loading: true,
  error: null,
  steps: [],
  currentStepIndex: 0,
  registrationId: null,
  userId: null,
  isCheckingRegistration: false,
  pricingSettings: null,
  criteriaSettings: null,
  hasUserInteracted: false,
  registrationData: {
    roster: [],
    registrationDancers: [],
    registrationTeachers: [],
    registrationObservers: [],
    dancersFromRoutines: [],
    dancerIds: [],
    teacherIds: [],
    observerIds: [],
    competition: {
      selectedRoutineIds: [],
      mappings: {},
      routineOptions: {}
    },
    pricing: {
      subtotal: 0,
      discounts: [],
      total: 0,
    },
    routines: []
  },
  regParticipants: {
    dancers: [],
    teachers: [],
    observers: []
  }
};

// Reducer function to handle all state updates
function registrationReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ActionTypes.SET_EVENT:
      return { ...state, event: action.payload };
    
    case ActionTypes.SET_STEPS:
      return { ...state, steps: action.payload };
    
    case ActionTypes.SET_CURRENT_STEP_INDEX:
      return { ...state, currentStepIndex: action.payload };
    
    case ActionTypes.SET_REGISTRATION_ID:
      return { ...state, registrationId: action.payload };
    
    case ActionTypes.SET_USER_ID:
      return { ...state, userId: action.payload };
    
    case ActionTypes.SET_CHECKING_REGISTRATION:
      return { ...state, isCheckingRegistration: action.payload };
    
    case ActionTypes.SET_SETTINGS:
      return { 
        ...state, 
        pricingSettings: action.payload.pricingSettings,
        criteriaSettings: action.payload.criteriaSettings 
      };
    
    case ActionTypes.SET_USER_INTERACTED:
      return { ...state, hasUserInteracted: action.payload };
    
    case ActionTypes.UPDATE_REGISTRATION_DATA:
      return { 
        ...state, 
        registrationData: {
          ...state.registrationData,
          ...action.payload
        }
      };
    
    case ActionTypes.SET_REG_PARTICIPANTS:
      return {
        ...state,
        regParticipants: action.payload,
        registrationData: {
          ...state.registrationData,
          regParticipants: action.payload
        }
      };
    
    default:
      return state;
  }
}

// Helper function to preprocess data before updating
const preprocessData = (stepId, data) => {
  // If data is a SyntheticEvent, extract a simple completion object
  if (data && data.nativeEvent && data._reactName) {
    return {
      completed: true,
      timestamp: new Date().toISOString()
    };
  }
  
  // For empty data, create a minimal completion object
  if (data === undefined || data === null) {
    return {
      completed: true,
      timestamp: new Date().toISOString()
    };
  }
  
  // Return the data as is for normal cases
  return data;
};

// Configure the registration steps based on event features
export function configureRegistrationSteps(eventData, allSteps) {
  let configuredSteps = [
    allSteps[0], // Always include overview
  ];

  // Use flags from eventData.eventTypeInfo
  const typeInfo = eventData.eventTypeInfo || {};

  // Add competition step if event type has competition
  if (typeInfo.hasCompetition) {
    configuredSteps.push(allSteps[1]); // competition (Routines)
  }

  // Add dancers step if event type has convention/workshop
  if (typeInfo.hasConvention) {
    configuredSteps.push(allSteps[2]); // dancers
  }

  // Add teachers step if event type has teachers
  if (typeInfo.hasTeachers) {
    configuredSteps.push(allSteps[3]); // teachers
  }

  // Add observers step if event type has observers
  if (typeInfo.hasObservers) {
    configuredSteps.push(allSteps[4]); // observers
  }

  // Always include the final steps
  configuredSteps = [
    ...configuredSteps,
    allSteps[5], // review
    allSteps[6], // payment
    allSteps[7], // success
  ];

  return configuredSteps;
}

/**
 * Custom hook for managing registration state
 */
export default function useRegistrationState(allSteps) {
  const { eventId: paramEventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get eventId from query parameters if available, otherwise fall back to path parameter
  const searchParams = new URLSearchParams(location.search);
  const queryEventId = searchParams.get('eventId');
  const eventId = queryEventId || paramEventId;
  
  // Set up reducer
  const [state, dispatch] = useReducer(registrationReducer, initialState);
  
  // Ref to track if we've already processed the registration to prevent infinite loops
  const processedRegistrationRef = useRef(null);
  
  // Destructure state for easier access
  const {
    event,
    loading,
    error,
    steps,
    currentStepIndex,
    registrationId,
    userId,
    isCheckingRegistration,
    pricingSettings,
    criteriaSettings,
    hasUserInteracted,
    registrationData,
    regParticipants
  } = state;

  // Dispatch helper functions
  const setLoading = (value) => dispatch({ type: ActionTypes.SET_LOADING, payload: value });
  const setError = (value) => dispatch({ type: ActionTypes.SET_ERROR, payload: value });
  const setEvent = (value) => dispatch({ type: ActionTypes.SET_EVENT, payload: value });
  const setSteps = (value) => dispatch({ type: ActionTypes.SET_STEPS, payload: value });
  const setCurrentStepIndex = (value) => dispatch({ type: ActionTypes.SET_CURRENT_STEP_INDEX, payload: value });
  const setRegistrationId = (value) => dispatch({ type: ActionTypes.SET_REGISTRATION_ID, payload: value });
  const setUserId = (value) => dispatch({ type: ActionTypes.SET_USER_ID, payload: value });
  const setCheckingRegistration = (value) => dispatch({ type: ActionTypes.SET_CHECKING_REGISTRATION, payload: value });
  const setSettings = (pricing, criteria) => dispatch({ 
    type: ActionTypes.SET_SETTINGS, 
    payload: { pricingSettings: pricing, criteriaSettings: criteria } 
  });
  const setUserInteracted = (value) => dispatch({ type: ActionTypes.SET_USER_INTERACTED, payload: value });
  const updateRegistrationData = (value) => dispatch({ type: ActionTypes.UPDATE_REGISTRATION_DATA, payload: value });
  const setRegParticipants = (value) => dispatch({ type: ActionTypes.SET_REG_PARTICIPANTS, payload: value });

  // Fetch current user
  const userQuery = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const currentUser = await getCurrentUser();
        return currentUser.userId || currentUser.username;
      } catch (err) {
        throw new Error("Error retrieving user information. Please try again or log in.");
      }
    }
  });

  // Fetch event and settings
  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => registrationService.fetchEventAndSettings(eventId),
    enabled: !!eventId
  });

  // Check for existing registration
  const registrationQuery = useQuery({
    queryKey: ['registration', eventId, userId],
    queryFn: () => registrationService.checkExistingRegistration(eventId, userId),
    enabled: !!eventId && !!userId && !isCheckingRegistration
  });

  // Get registration details
  const registrationDetailsQuery = useQuery({
    queryKey: ['registrationDetails', registrationId],
    queryFn: () => registrationService.deduplicateAndHydrateRegistrationData(registrationId),
    enabled: !!registrationId
  });

  // Get registration participants
  const participantsQuery = useQuery({
    queryKey: ['participants', registrationId],
    queryFn: () => registrationService.fetchRegistrationParticipants(registrationId),
    enabled: !!registrationId
  });

  // Create registration mutation
  const createRegistrationMutation = useMutation({
    mutationFn: (data) => registrationService.createNewRegistration(data.eventId, data.userId, data.eventData),
    onSuccess: (data) => {
      if (data && data.registrationId) {
        setRegistrationId(data.registrationId);
        queryClient.invalidateQueries({ queryKey: ['registrationDetails', data.registrationId] });
        queryClient.invalidateQueries({ queryKey: ['participants', data.registrationId] });
      } else {
        setError('Failed to create registration - no registration ID returned');
      }
    },
    onError: (error) => {
      console.error('Registration creation error:', error);
      setError(error.message || 'Failed to create registration. Please try again.');
      setLoading(false);
    }
  });

  // Update registration mutation
  const updateRegistrationMutation = useMutation({
    mutationFn: (data) => registrationService.updateRegistrationDirectly(
      data.registrationId, 
      data.stepId, 
      data.userId, 
      data.eventId, 
      data.data, 
      data.isStepComplete
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
    }
  });

  // Add participant mutation
  const addParticipantMutation = useMutation({
    mutationFn: (mutationData) => registrationService.addParticipantToRegistration(
      mutationData.registrationId, 
      mutationData.participant, 
      mutationData.type
    ),
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully added ${mutationData.type}:`, result);
      queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error adding ${mutationData.type}:`, error);
      console.error('Failed participant data:', mutationData);
    }
  });

  // Update participant mutation
  const updateParticipantMutation = useMutation({
    mutationFn: (mutationData) => registrationService.updateRegistrationParticipant(
      mutationData.registrationId, 
      mutationData.participantId, 
      mutationData.updateData
    ),
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully updated participant ${mutationData.participantId}:`, result);
      queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error updating participant ${mutationData.participantId}:`, error);
      console.error('Failed update data:', mutationData);
    }
  });

  // Remove participant mutation
  const removeParticipantMutation = useMutation({
    mutationFn: (mutationData) => registrationService.removeParticipantFromRegistration(
      mutationData.registrationId, 
      mutationData.participantId
    ),
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully removed participant ${mutationData.participantId}:`, result);
      queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error removing participant ${mutationData.participantId}:`, error);
    }
  });

  // Add routine mutation
  const addRoutineMutation = useMutation({
    mutationFn: (mutationData) => registrationService.addRoutineToRegistration(
      mutationData.registrationId, 
      mutationData.routineId, 
      mutationData.options
    ),
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully added routine ${mutationData.routineId}:`, result);
      
      // Update the routine mapping to store the new registered routine ID
      if (result && result.registrationRoutineId) {
        console.log(`📝 Mapping clone ID ${mutationData.routineId} to registered ID ${result.registrationRoutineId}`);
        
        // Store the mapping for future updates
        const updatedRoutines = (registrationData.routines || []).map(routine => {
          // Find the routine that was just created and update its ID
          if (routine.originalRoutineId === mutationData.routineId || 
              routine.routineId === mutationData.routineId ||
              routine.routineCloneId?.includes(mutationData.routineId)) {
            return {
              ...routine,
              registeredRoutineId: result.registrationRoutineId, // Store the registered ID
              pendingCreation: false
            };
          }
          return routine;
        });
        
        dispatch({ 
          type: ActionTypes.UPDATE_REGISTRATION_DATA, 
          payload: { routines: updatedRoutines } 
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error adding routine ${mutationData.routineId}:`, error);
      console.error('Failed routine data:', mutationData);
    }
  });

  // Helper function to resolve the correct routine ID for API calls
  const resolveRoutineId = useCallback((routineCloneId) => {
    if (!registrationData || !registrationData.routines) {
      return routineCloneId;
    }
    
    // Find the routine and get the registered ID if available
    const routine = registrationData.routines.find(r => 
      r.routineCloneId === routineCloneId || 
      r.routineId === routineCloneId ||
      r.originalRoutineId === routineCloneId
    );
    
    // Use registered ID if available, otherwise fall back to clone ID
    const resolvedId = routine?.registeredRoutineId || routineCloneId;
    console.log(`🔍 Resolving routine ID: ${routineCloneId} → ${resolvedId}`);
    return resolvedId;
  }, [registrationData]);

  // Update routine mutation
  const updateRoutineMutation = useMutation({
    mutationFn: (mutationData) => {
      const resolvedRoutineId = resolveRoutineId(mutationData.routineCloneId);
      return registrationService.updateRegistrationRoutine(
        mutationData.registrationId, 
        resolvedRoutineId, 
        mutationData.updateData
      );
    },
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully updated routine ${mutationData.routineCloneId}:`, result);
      queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error updating routine ${mutationData.routineCloneId}:`, error);
      console.error('Failed routine update data:', mutationData);
    }
  });

  // Remove routine mutation
  const removeRoutineMutation = useMutation({
    mutationFn: (mutationData) => {
      const resolvedRoutineId = resolveRoutineId(mutationData.routineCloneId);
      return registrationService.removeRoutineFromRegistration(
        mutationData.registrationId, 
        resolvedRoutineId
      );
    },
    onSuccess: (result, mutationData) => {
      console.log(`✅ Successfully removed routine ${mutationData.routineCloneId}:`, result);
      queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
    },
    onError: (error, mutationData) => {
      console.error(`❌ Error removing routine ${mutationData.routineCloneId}:`, error);
    }
  });

  // Handle user query side effects
  useEffect(() => {
    if (userQuery.isSuccess && userQuery.data) {
      setUserId(userQuery.data);
    }
    if (userQuery.isError) {
      setError(userQuery.error?.message || 'Failed to load user data');
    }
  }, [userQuery.isSuccess, userQuery.isError, userQuery.data, userQuery.error]);

  // Handle event query side effects
  useEffect(() => {
    if (eventQuery.isSuccess && eventQuery.data) {
      setEvent(eventQuery.data.event);
      setSettings(eventQuery.data.pricingSettings, eventQuery.data.criteriaSettings);
      const configuredSteps = configureRegistrationSteps(eventQuery.data.event, allSteps);
      setSteps(configuredSteps);
      setLoading(false);
    }
    if (eventQuery.isError) {
      setError(eventQuery.error?.message || 'Failed to load event data');
      setLoading(false);
    }
  }, [eventQuery.isSuccess, eventQuery.isError, eventQuery.data, eventQuery.error, allSteps]);

  // Handle registration query side effects
  useEffect(() => {
    if (registrationQuery.isSuccess && registrationQuery.data) {
      const data = registrationQuery.data;
      const regId = data.registrationId;
      
      // Prevent infinite loops by checking if we've already processed this registration
      if (processedRegistrationRef.current === regId) {
        return;
      }
      
      console.log('Registration query result:', data);
      console.log('Found existing registration:', regId);
      
      // Mark this registration as processed and set the ID
      processedRegistrationRef.current = regId;
      setRegistrationId(regId);
    }
    
    if (registrationQuery.isError) {
      console.error("Error checking registration:", registrationQuery.error);
      setError(`Error checking registration: ${registrationQuery.error?.message}`);
    }
  }, [registrationQuery.isSuccess, registrationQuery.isError, registrationQuery.data, registrationQuery.error]);

  // Handle navigation after registration is found (separate effect to avoid dependency issues)
  useEffect(() => {
    if (registrationId && steps.length > 0 && registrationQuery.data) {
      const data = registrationQuery.data;
      // Only navigate if we haven't already navigated for this registration
      const navigationKey = `nav-${registrationId}`;
      if (processedRegistrationRef.current !== navigationKey) {
        processedRegistrationRef.current = navigationKey;
        
        if (data.lastCompletedStep) {
          const stepIndex = steps.findIndex(
            (step) => step.id === data.lastCompletedStep
          );
          if (stepIndex >= 0 && stepIndex + 1 < steps.length) {
            const nextStepPath = steps[stepIndex + 1].path;
            navigate(`/user/registration/${nextStepPath}?eventId=${eventId}`);
          } else {
            navigate(`/user/registration/${steps[0].path}?eventId=${eventId}`);
          }
        } else if (data.currentStepPath) {
          navigate(`/user/registration/${data.currentStepPath}?eventId=${eventId}`);
        } else {
          navigate(`/user/registration/${steps[0].path}?eventId=${eventId}`);
        }
      }
    }
  }, [registrationId, steps, eventId, navigate, registrationQuery.data]);

  // Handle new registration creation (separate effect)
  useEffect(() => {
    if (registrationQuery.isSuccess && !registrationQuery.data && eventId && userId && event && !registrationId) {
      const key = `create-${eventId}-${userId}`;
      if (processedRegistrationRef.current !== key && !loading) {
        console.log('No existing registration found, creating new registration for event:', eventId, 'and user:', userId);
        processedRegistrationRef.current = key;
        setLoading(true);
        createRegistrationMutation.mutate({
          eventId,
          userId,
          eventData: event
        });
      }
    }
  }, [registrationQuery.isSuccess, registrationQuery.data, eventId, userId, event, registrationId, loading, createRegistrationMutation]);

  // Handle registration details query side effects
  useEffect(() => {
    if (registrationDetailsQuery.isSuccess && registrationDetailsQuery.data) {
      updateRegistrationData(registrationDetailsQuery.data);
    }
  }, [registrationDetailsQuery.isSuccess, registrationDetailsQuery.data]);

  // Handle participants query side effects
  useEffect(() => {
    if (participantsQuery.isSuccess && participantsQuery.data) {
      setRegParticipants(participantsQuery.data);
    }
  }, [participantsQuery.isSuccess, participantsQuery.data]);

  // Create a function to create a new registration
  const createNewRegistration = useCallback(() => {
    if (!eventId || !userId || !event) {
      console.error('Missing required data for registration creation', { eventId, userId, eventLoaded: !!event });
      setError('Missing required data for registration creation');
      return;
    }
    
    console.log('=== CREATING NEW REGISTRATION ===');
    console.log('Event ID:', eventId);
    console.log('User ID:', userId);
    console.log('Event data:', event);
    console.log('Steps configured:', steps.length);
    
    setLoading(true);
    
    createRegistrationMutation.mutate({
      eventId,
      userId,
      eventData: event
    }, {
      onSuccess: (data) => {
        console.log('=== REGISTRATION CREATION SUCCESS ===');
        console.log('Response data:', data);
        console.log('Registration ID:', data?.registrationId);
        
        setLoading(false);
        if (data && data.registrationId) {
          console.log('Setting registration ID:', data.registrationId);
          setRegistrationId(data.registrationId);
          
          // Navigate to first step
          if (steps.length > 0) {
            const firstStepPath = steps[0].path;
            console.log('Navigating to first step:', firstStepPath);
            navigate(`/user/registration/${firstStepPath}?eventId=${eventId}`);
          } else {
            console.error('No steps configured for navigation');
            setError('No registration steps configured');
          }
        } else {
          console.error('=== REGISTRATION CREATION ERROR ===');
          console.error('Registration creation response missing registrationId:', data);
          setError('Failed to create registration - no registration ID returned');
        }
      },
      onError: (error) => {
        console.error('=== REGISTRATION CREATION FAILED ===');
        console.error('Error details:', error);
        console.error('Error message:', error.message);
        console.error('Error status:', error.status);
        console.error('Error response:', error.responseData);
        
        setLoading(false);
        
        // Handle specific error cases
        if (error.status === 409) {
          console.log('Registration already exists, attempting to fetch existing registration');
          // Try to fetch the existing registration
          registrationService.checkExistingRegistration(eventId, userId)
            .then((existingReg) => {
              if (existingReg && existingReg.registrationId) {
                console.log('Found existing registration:', existingReg.registrationId);
                setRegistrationId(existingReg.registrationId);
                if (steps.length > 0) {
                  navigate(`/user/registration/${steps[0].path}?eventId=${eventId}`);
                }
              } else {
                setError('Registration conflict - unable to retrieve existing registration');
              }
            })
            .catch((fetchError) => {
              console.error('Error fetching existing registration:', fetchError);
              setError('Registration conflict - unable to retrieve existing registration');
            });
        } else {
          setError(`Registration creation failed: ${error.message || 'Unknown error'}`);
        }
      }
    });
  }, [eventId, userId, event, steps, navigate, createRegistrationMutation, setRegistrationId, setLoading, setError]);

  // Main function to update registration data
  const handleUpdateRegistrationData = useCallback(async (
    stepId,
    data,
    isStepComplete = false
  ) => {
    // Skip if we don't have registration ID, event, or event ID
    if (!registrationId || !event || !event.id) {
      return;
    }

    // Determine if we should update the backend
    const shouldUpdateBackend = hasUserInteracted || isStepComplete;
    
    // --- 1. Process data for local state update ---
    let validatedDataForState = preprocessData(stepId, data);
    let stateUpdatePayload = { [stepId]: validatedDataForState };

    // Handle specific data structures for local state update
    if (stepId === "dancers" && Array.isArray(validatedDataForState)) {
      const dancerIds = validatedDataForState.map(d => d.dancerId);
      stateUpdatePayload = {
        registrationDancers: validatedDataForState,
        dancerIds: dancerIds
      };
    } else if (stepId === "teachers" && Array.isArray(validatedDataForState)) {
      const teacherIds = validatedDataForState.map(t => t.teacherId);
      stateUpdatePayload = {
        registrationTeachers: validatedDataForState,
        teacherIds: teacherIds
      };
    } else if (stepId === "observers" && Array.isArray(validatedDataForState)) {
      const observerIds = validatedDataForState.map(o => o.observerId);
      stateUpdatePayload = {
        registrationObservers: validatedDataForState,
        observerIds: observerIds
      };
    } else if (stepId === "competition" && validatedDataForState) {
      // Special handling for competition step routine operations
      if (data?.updatingRoutine) {
        const { routineId, updateData } = data.updatingRoutine;
        
        if (!routineId || routineId === "undefined") {
          return;
        }
        
        updateRoutineMutation.mutate({
          registrationId,
          routineCloneId: routineId,
          updateData
        });
        return;
      } else if (data?.addingRoutine) {
        const { routineId, style, type, typeLabel, averageAge, ageGroup, ...otherOptions } = data.addingRoutine;
        
        if (!routineId || routineId === "undefined") {
          return;
        }
        
        addRoutineMutation.mutate({
          registrationId,
          routineId,
          options: { style, type, typeLabel, averageAge, ageGroup, ...otherOptions }
        });
        return;
      } else if (data?.removingRoutine) {
        const { routineId } = data.removingRoutine;
        
        if (!routineId || routineId === "undefined") {
          return;
        }
        
        removeRoutineMutation.mutate({
          registrationId,
          routineCloneId: routineId
        });
        return;
      }
      
      // For other updates to the competition data
      stateUpdatePayload = {
        competition: {
          selectedRoutineIds: data.selectedRoutineIds || registrationData.competition?.selectedRoutineIds || [],
          mappings: data.mappings || registrationData.competition?.mappings || {},
          routineOptions: data.routineOptions || registrationData.competition?.routineOptions || {}
        }
      };
    } else if (stepId === "pricing" && validatedDataForState) {
      stateUpdatePayload = { pricing: validatedDataForState };
    } else if (stepId === "overview" && validatedDataForState) {
      stateUpdatePayload = {
        overview: {
          ...(registrationData.overview || {}),
          ...validatedDataForState
        }
      };
    }

    // Update local state
    updateRegistrationData(stateUpdatePayload);

    // --- 2. Prepare and send data to backend if required ---
    if (!shouldUpdateBackend || SKIP_PERSISTENCE_STEPS.includes(stepId)) {
      return;
    }

    // Prepare API payload
    let validatedDataForApi = preprocessData(stepId, data);
    let apiUpdatePayload = {};

    // Structure the API payload similarly to the state update
    if (stepId === "dancers" && Array.isArray(validatedDataForApi)) {
      const dancerIds = validatedDataForApi.map(d => d.dancerId);
      apiUpdatePayload = {
        dancerIds: dancerIds,
        registrationDancers: validatedDataForApi
      };
    } else if (stepId === "teachers" && Array.isArray(validatedDataForApi)) {
      const teacherIds = validatedDataForApi.map(t => t.teacherId);
      apiUpdatePayload = {
        teacherIds: teacherIds,
        registrationTeachers: validatedDataForApi
      };
    } else if (stepId === "observers" && Array.isArray(validatedDataForApi)) {
      const observerIds = validatedDataForApi.map(o => o.observerId);
      apiUpdatePayload = {
        observerIds: observerIds,
        registrationObservers: validatedDataForApi
      };
    } else if (stepId === "competition" && validatedDataForApi) {
      // For routine operations, we've already handled them above
      if (data?.updatingRoutine || data?.addingRoutine || data?.removingRoutine) {
        return;
      }
      
      apiUpdatePayload = {
        competition: {
          selectedRoutineIds: data.selectedRoutineIds || registrationData.competition?.selectedRoutineIds || [],
          mappings: data.mappings || registrationData.competition?.mappings || {},
          routineOptions: data.routineOptions || registrationData.competition?.routineOptions || {}
        }
      };
    } else if (stepId === "pricing" && validatedDataForApi) {
      apiUpdatePayload = { pricing: validatedDataForApi };
    } else {
      // Default: use the stepId as the key for the data
      apiUpdatePayload[stepId] = validatedDataForApi;
    }

    // Add completion fields if step is marked complete
    if (isStepComplete) {
      apiUpdatePayload.lastCompletedStep = stepId;
      const completedStepPath = steps.find(s => s.id === stepId)?.path;
      if (completedStepPath) {
        apiUpdatePayload.lastCompletedStepPath = completedStepPath;
      }
    }

    // Add current step info if present in the original data
    if (data?.currentStep !== undefined) {
      apiUpdatePayload.currentStep = data.currentStep;
    }
    if (data?.currentStepPath) {
      apiUpdatePayload.currentStepPath = data.currentStepPath;
    }

    // Call the update mutation
    updateRegistrationMutation.mutate({
      registrationId,
      stepId,
      userId,
      eventId: event.id,
      data: apiUpdatePayload,
      isStepComplete
    });
  }, [
    registrationId, 
    event, 
    userId, 
    hasUserInteracted, 
    registrationData, 
    steps, 
    updateRegistrationMutation,
    addRoutineMutation,
    updateRoutineMutation,
    removeRoutineMutation
  ]);

  // Handle navigation updates
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldReloadData = searchParams.get('reload') === 'true';
    
    // Force data reload if requested
    if (shouldReloadData && registrationId && !window._initialDataLoaded) {
      window._initialDataLoaded = true;
      queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
      queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
    }
    
    // Update current step based on URL path
    if (steps.length > 0 && event) {
      const path = location.pathname.split("/").pop();
      const stepIndex = steps.findIndex((step) => step.path === path);

      if (stepIndex >= 0 && stepIndex !== currentStepIndex) {
        setCurrentStepIndex(stepIndex);
        const currentStep = steps[stepIndex];
        
        // Force reload data if requested on navigation
        if (shouldReloadData && registrationId && !window._arrivedWithReload) {
          window._arrivedWithReload = true;
          queryClient.invalidateQueries({ queryKey: ['registrationDetails', registrationId] });
          queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
        }
        
        // Update registration data with navigation info
        if (registrationId && event.id && userId) {
          const navigationKey = `${registrationId}-${event.id}-${stepIndex}`;
          
          if (!window._lastProcessedNavigation || window._lastProcessedNavigation !== navigationKey) {
            window._lastProcessedNavigation = navigationKey;
            
            const arrivalData = {
              visited: true,
              arrivalTimestamp: new Date().toISOString(),
              currentStep: stepIndex, 
              currentStepPath: currentStep.path 
            };
            
            handleUpdateRegistrationData(currentStep.id, arrivalData, false);
          }
        }
      } else if (steps.length > 0 && stepIndex < 0) {
        // If no matching step found, redirect to the first step
        navigate(`/user/registration/${steps[0].path}?eventId=${eventId}`, {
          replace: true,
        });
      }
    }
  }, [
    location.pathname, 
    location.search, 
    steps, 
    event, 
    eventId, 
    navigate, 
    registrationId, 
    userId, 
    handleUpdateRegistrationData, 
    queryClient,
    currentStepIndex
  ]);

  // Helper function to update step progression in database
  const updateStepProgression = useCallback(async (stepIndex, stepPath, markCompleted = false) => {
    if (!registrationId || !userId || !event?.id) {
      console.log('Missing data for step progression update:', { registrationId, userId, eventId: event?.id });
      return;
    }

    try {
      console.log(`Updating step progression: ${stepIndex} (${stepPath})${markCompleted ? ' - COMPLETED' : ''}`);
      
      const updateData = {
        currentStep: stepIndex,
        currentStepPath: stepPath,
        ...(markCompleted && { 
          lastCompletedStep: stepPath,
          lastCompletedStepIndex: stepIndex
        }),
        updatedAt: new Date().toISOString()
      };

      // Use the existing update registration mutation
      updateRegistrationMutation.mutate({
        registrationId,
        stepId: 'navigation',
        userId,
        eventId: event.id,
        data: updateData,
        isStepComplete: markCompleted
      });
    } catch (error) {
      console.error('Error updating step progression:', error);
    }
  }, [registrationId, userId, event, updateRegistrationMutation]);

  // Handle click on step in stepper UI
  const handleStepClick = (index) => {
    setUserInteracted(true);
    
    // Only allow navigating to accessible steps (not more than one ahead)
    if (index <= currentStepIndex + 1) {
      const targetStepPath = steps[index].path;
      
      // Update step progression in database
      updateStepProgression(index, targetStepPath);
      
      navigate(`/user/registration/${targetStepPath}?eventId=${eventId}`);
    }
  };

  // Handle navigation to next step
  const handleNext = () => {
    setUserInteracted(true);
    
    if (currentStepIndex < steps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      const nextStepPath = steps[nextStepIndex].path;
      
      // Mark current step as completed and update to next step
      updateStepProgression(nextStepIndex, nextStepPath, true);
      
      navigate(`/user/registration/${nextStepPath}?eventId=${eventId}`);
    }
  };

  // Handle navigation to previous step
  const handleBack = () => {
    setUserInteracted(true);
    
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      const prevStepPath = steps[prevStepIndex].path;
      
      // Update step progression in database
      updateStepProgression(prevStepIndex, prevStepPath);
      
      navigate(`/user/registration/${prevStepPath}?eventId=${eventId}`);
    }
  };

  // Create participant helper functions
  const addParticipant = useCallback((participant, type) => {
    if (!registrationId) return null;
    
    return addParticipantMutation.mutate({
      registrationId,
      participant,
      type
    });
  }, [registrationId, addParticipantMutation]);

  const updateParticipant = useCallback((participantId, updateData) => {
    if (!registrationId) return null;
    
    return updateParticipantMutation.mutate({
      registrationId,
      participantId,
      updateData
    });
  }, [registrationId, updateParticipantMutation]);

  const removeParticipant = useCallback((participantId) => {
    if (!registrationId) return null;
    
    return new Promise((resolve, reject) => {
      removeParticipantMutation.mutate({
        registrationId,
        participantId
      }, {
        onSuccess: (data) => {
          console.log("Successfully removed participant:", participantId);
          // Invalidate the participants query to refresh the data
          queryClient.invalidateQueries({ queryKey: ['participants', registrationId] });
          resolve(data);
        },
        onError: (error) => {
          console.error("Error removing participant:", error);
          // Pass the error to the caller
          reject(error);
        }
      });
    });
  }, [registrationId, removeParticipantMutation, queryClient]);

  // Add routine mutation
  const addRoutine = useCallback((routineId, options) => {
    if (!registrationId) return null;
    
    return addRoutineMutation.mutate({
      registrationId,
      routineId,
      options
    });
  }, [registrationId, addRoutineMutation]);

  // Update routine mutation
  const updateRoutine = useCallback((routineCloneId, updateData) => {
    if (!registrationId) return null;
    
    return updateRoutineMutation.mutate({
      registrationId,
      routineCloneId,
      updateData
    });
  }, [registrationId, updateRoutineMutation]);

  // Remove routine mutation
  const removeRoutine = useCallback((routineCloneId) => {
    if (!registrationId) return null;
    
    return removeRoutineMutation.mutate({
      registrationId,
      routineCloneId
    });
  }, [registrationId, removeRoutineMutation]);

  // Return state and handlers
  return {
    state: {
      event,
      loading,
      error,
      steps,
      currentStepIndex,
      registrationId,
      userId,
      pricingSettings,
      criteriaSettings,
      registrationData,
      regParticipants,
    },
    handlers: {
      handleStepClick,
      handleNext,
      handleBack,
      updateRegistrationData: handleUpdateRegistrationData,
      addParticipant,
      updateParticipant,
      removeParticipant,
      addRoutine,
      updateRoutine,
      removeRoutine,
    }
  };
} 